import { useCallback, useEffect } from 'react';

import useTimeout from './useTimeout';

/**
 * A list of events that we will monitor to determine if user is still active.
 */
const EVENTS_ARRAY = [
    'keypress',
    'keydown',
    'click',
    'contextmenu',
    'dblclick',
    'mousemove',
    'scroll',
    'touchmove',
    'touchstart',
];

export type UseUserSessionResult = {
    /**
     * If the user session is still active
     */
    isSessionActive: boolean;

    /**
     * How long time left of the session in seconds
     */
    sessionTimeLeft: number;

    /**
     * A function for resetting and extending the session time
     */
    resetSession: () => void;
};

/**
 * A hook that will track the user interactions and keep track of the user session time.
 *
 * @param maxIdleTime the maximum time for the user to be idle
 */
export const useUserSession = (maxIdleTime = 10): UseUserSessionResult => {
    const { start, reset, isStarted, timeLeft } = useTimeout(maxIdleTime, true);

    const resetSession = useCallback(() => {
        reset();
        start();
    }, [reset, start]);

    // Register event listeners for user events to determine if user is idle
    useEffect(() => {
        const eventListeners = [];
        if (isStarted) {
            EVENTS_ARRAY.forEach((event) => {
                eventListeners.push(addEventListener(event, resetSession));
            });
        }

        return () => {
            EVENTS_ARRAY.forEach((event) => {
                eventListeners.push(removeEventListener(event, resetSession));
            });
        };
    }, [isStarted]);

    return { isSessionActive: isStarted, sessionTimeLeft: timeLeft, resetSession };
};

export default useUserSession;
